import React from "react";
import { useUsageLimits } from "@/hooks/pricing/useUsageLimits";
import { MessageSquare, Wrench, Coins } from "lucide-react";

interface ChatMessageUsageIndicatorProps {
  promptsConsumed?: number;
  toolCallsCount?: number;
  inputTokens?: number;
  outputTokens?: number;
}

export const ChatMessageUsageIndicator: React.FC<
  ChatMessageUsageIndicatorProps
> = ({ promptsConsumed, toolCallsCount, inputTokens, outputTokens }) => {
  const { remainingUsage } = useUsageLimits();
  const totalTokens = (inputTokens || 0) + (outputTokens || 0);

  // Determine if we should show tokens or prompts based on subscription tier
  // Show tokens for users NOT on free tier and NOT starting with 'new'
  // Show prompts for everyone else (free tier and new_* tiers)
  const shouldShowTokens =
    remainingUsage.subscriptionTier &&
    remainingUsage.subscriptionTier !== "free" &&
    !remainingUsage.subscriptionTier.startsWith("new");

  const hasMetrics =
    toolCallsCount || (shouldShowTokens ? totalTokens > 0 : promptsConsumed);

  if (!hasMetrics) {
    return null;
  }

  const formatTokenCount = (count: number) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  return (
    <div className="mt-2 mb-4 flex flex-wrap gap-3 text-xs text-gray-500">
      {shouldShowTokens
        ? // Show tokens for old tier users (not free, not new_*)
          totalTokens > 0 && (
            <span className="flex items-center gap-1">
              <Coins className="w-3 h-3" />
              {formatTokenCount(totalTokens)} tokens consumed
            </span>
          )
        : // Show prompts for free and new_* tier users
          promptsConsumed && (
            <span className="flex items-center gap-1">
              <Coins className="w-3 h-3" />
              {promptsConsumed} prompt{promptsConsumed !== 1 ? "s" : ""}{" "}
              consumed
            </span>
          )}
      {toolCallsCount && (
        <span className="flex items-center gap-1">
          <Wrench className="w-3 h-3" />
          {toolCallsCount} tool call{toolCallsCount !== 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
};
